import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Box,
    Checkbox,
    Chip,
    FormControlLabel,
    LinearProgress,
    Paper,
    Stack,
    Tooltip,
    Typography,
    useTheme,
} from '@mui/material';
import {
    BatteryFull as BatteryIcon,
    Bolt as CurrentIcon,
    Thermostat as TempIcon,
} from '@mui/icons-material';
import ConfirmationDialog from './ConfirmationDialog';
import ParamRow, { getParamEntry } from './ParamRow';

// ArduPilot battery params: instance 0 -> BATT_*, instance 1 -> BATT2_*, ..., instance 8 -> BATT9_*
const NUM_INSTANCES = 9;
const prefixFor = (idx) => (idx === 0 ? 'BATT_' : `BATT${idx + 1}_`);

const PARAM_GROUPS = [
    {
        title: 'Basic',
        rows: [
            { suffix: 'MONITOR',    desc: 'Battery monitor type / source.' },
            { suffix: 'CAPACITY',   desc: 'Designed pack capacity (mAh).' },
            { suffix: 'SERIAL_NUM', desc: 'Serial number of the smart battery.' },
            { suffix: 'OPTIONS',    desc: 'Battery options bitmask.' },
        ],
    },
    {
        title: 'Low Failsafe',
        rows: [
            { suffix: 'FS_LOW_ACT', desc: 'Action on low battery.' },
            { suffix: 'LOW_VOLT',   desc: 'Low battery voltage threshold (V).' },
            { suffix: 'LOW_MAH',    desc: 'Low battery used-capacity threshold (mAh).' },
            { suffix: 'LOW_TIMER',  desc: 'Seconds below threshold before failsafe.' },
        ],
    },
    {
        title: 'Critical Failsafe',
        rows: [
            { suffix: 'FS_CRT_ACT', desc: 'Action on critical battery.' },
            { suffix: 'CRT_VOLT',   desc: 'Critical battery voltage threshold (V).' },
            { suffix: 'CRT_MAH',    desc: 'Critical used-capacity threshold (mAh).' },
        ],
    },
    {
        title: 'Calibration',
        rows: [
            { suffix: 'FS_VOLTSRC', desc: 'Voltage source for failsafe (raw / sag-comp).' },
            { suffix: 'AMP_PERVLT', desc: 'Amps per volt of current sensor.' },
            { suffix: 'VOLT_MULT',  desc: 'Voltage divider multiplier.' },
            { suffix: 'AMP_OFFSET', desc: 'Current sensor offset (V).' },
            { suffix: 'VOLT_PIN',   desc: 'Analog voltage pin.' },
            { suffix: 'CURR_PIN',   desc: 'Analog current pin.' },
        ],
    },
];

const fmt = (v, digits = 2, fallback = '\u2014') => {
    if (v == null || !Number.isFinite(Number(v))) return fallback;
    return Number(v).toFixed(digits);
};

const sumCellVoltages = (voltages) => {
    if (!voltages || !Array.isArray(voltages)) return 0;
    let total = 0;
    for (const v of voltages) {
        // 65535 means cell not present; 0xFFFF in mV
        if (v == null || v === 65535 || v === 0) continue;
        total += v;
    }
    return total;
};

const countActiveCells = (voltages) => {
    if (!voltages || !Array.isArray(voltages)) return 0;
    let n = 0;
    for (const v of voltages) {
        if (v != null && v !== 0 && v !== 65535) n++;
    }
    return n;
};

const remainingColor = (pct) => {
    if (pct == null) return 'inherit';
    if (pct >= 50) return 'success.main';
    if (pct >= 20) return 'warning.main';
    return 'error.main';
};

const BatteryCard = ({
    idx, status, prefix, mavId, disabled, onCommit, theme, paramListTimestamp, forceShow,
}) => {
    const monitorEntry = getParamEntry(mavId, `${prefix}MONITOR`);
    const monitorVal = monitorEntry ? Number(monitorEntry.value) : 0;
    const present = !!status;
    const enabled = monitorVal !== 0;

    if (!enabled && !present && !forceShow) return null;

    const totalV = present ? sumCellVoltages(status.voltages) / 1000 : null;
    const currentA = present && status.current_battery !== -1 && status.current_battery !== undefined
        ? status.current_battery / 100
        : null;
    const remainPct = present && status.battery_remaining !== -1 ? status.battery_remaining : null;
    const usedMah = present && status.current_consumed !== -1 ? status.current_consumed : null;
    const tempC = present && status.temperature !== undefined && status.temperature !== 32767
        ? status.temperature / 100
        : null;
    const cellCount = present ? countActiveCells(status.voltages) : 0;
    const cellAvg = cellCount > 0 && totalV != null ? totalV / cellCount : null;
    const timeRemainS = present && status.time_remaining !== undefined && status.time_remaining > 0
        ? status.time_remaining
        : null;

    return (
        <Box
            sx={{
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 1,
                p: 1.5,
                bgcolor: 'background.default',
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
            }}
        >
            {/* Header */}
            <Stack direction="row" alignItems="center" spacing={1}>
                <BatteryIcon
                    fontSize="small"
                    sx={{ color: present ? remainingColor(remainPct) : 'text.disabled' }}
                />
                <Typography variant="subtitle2">Battery {idx + 1}</Typography>
                <Chip
                    size="small"
                    label={present ? 'Live' : (enabled ? 'Configured' : 'Disabled')}
                    color={present ? 'success' : (enabled ? 'default' : 'default')}
                    variant={enabled || present ? 'filled' : 'outlined'}
                    sx={{ height: 20, fontSize: 11 }}
                />
                <Chip
                    size="small"
                    variant="outlined"
                    label={`Monitor ${monitorVal}`}
                    sx={{ height: 20, fontSize: 11 }}
                />
                {cellCount > 0 && (
                    <Chip
                        size="small"
                        variant="outlined"
                        label={`${cellCount}S`}
                        sx={{ height: 20, fontSize: 11 }}
                    />
                )}
                <Box sx={{ flex: 1 }} />
                {remainPct != null && (
                    <Typography
                        variant="caption"
                        sx={{ color: remainingColor(remainPct), fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}
                    >
                        {remainPct}%
                    </Typography>
                )}
            </Stack>

            {/* Body: telemetry on top (full width), config groups below in a horizontal row */}
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1.5,
                }}
            >
                {/* Telemetry section */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
                    {present && remainPct != null && (
                        <Tooltip title={`Remaining ${remainPct}%`} placement="top" arrow>
                            <LinearProgress
                                variant="determinate"
                                value={Math.max(0, Math.min(100, remainPct))}
                                sx={{
                                    height: 8,
                                    borderRadius: 1,
                                    bgcolor: theme.palette.action.hover,
                                    '& .MuiLinearProgress-bar': {
                                        bgcolor:
                                            remainPct >= 50
                                                ? theme.palette.success.main
                                                : remainPct >= 20
                                                    ? theme.palette.warning.main
                                                    : theme.palette.error.main,
                                    },
                                }}
                            />
                        </Tooltip>
                    )}

                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
                            columnGap: 1.5,
                            rowGap: 0.5,
                        }}
                    >
                        <Metric label="Voltage" value={fmt(totalV)} unit="V" />
                        <Metric label="Cell avg" value={fmt(cellAvg, 3)} unit="V" />
                        <Metric
                            label="Current"
                            value={fmt(currentA)}
                            unit="A"
                            icon={<CurrentIcon sx={{ fontSize: 12, color: 'text.disabled' }} />}
                        />
                        <Metric label="Used" value={usedMah != null ? String(usedMah) : '\u2014'} unit="mAh" />
                        <Metric
                            label="Temp"
                            value={fmt(tempC, 1)}
                            unit="\u00B0C"
                            icon={<TempIcon sx={{ fontSize: 12, color: 'text.disabled' }} />}
                        />
                        <Metric
                            label="Time left"
                            value={timeRemainS != null ? `${Math.floor(timeRemainS / 60)}m ${timeRemainS % 60}s` : '\u2014'}
                            unit=""
                        />
                    </Box>

                    {present && cellCount > 1 && (
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fill, minmax(72px, 1fr))',
                                gap: 0.5,
                            }}
                        >
                            {(status.voltages || []).slice(0, cellCount).map((mv, i) => (
                                <Chip
                                    key={i}
                                    size="small"
                                    variant="outlined"
                                    label={`C${i + 1} ${(mv / 1000).toFixed(3)}V`}
                                    sx={{ height: 18, fontSize: 10, fontVariantNumeric: 'tabular-nums' }}
                                />
                            ))}
                        </Box>
                    )}
                </Box>

                {/* Config groups: horizontal row of grouped param columns */}
                <Box
                    sx={{
                        pt: 1,
                        borderTop: `1px dashed ${theme.palette.divider}`,
                        display: 'grid',
                        gridTemplateColumns: {
                            xs: '1fr',
                            sm: 'repeat(2, minmax(0, 1fr))',
                            lg: `repeat(${PARAM_GROUPS.length}, minmax(0, 1fr))`,
                        },
                        columnGap: 2,
                        rowGap: 1,
                        alignItems: 'start',
                    }}
                >
                    {PARAM_GROUPS.map((group) => {
                        const visibleRows = group.rows.filter((r) => !!getParamEntry(mavId, `${prefix}${r.suffix}`));
                        if (visibleRows.length === 0) return null;
                        return (
                            <Box key={group.title} sx={{ minWidth: 0 }}>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: 'text.secondary',
                                        textTransform: 'uppercase',
                                        letterSpacing: 0.5,
                                        display: 'block',
                                        borderBottom: `1px solid ${theme.palette.divider}`,
                                        pb: 0.25,
                                        mb: 0.5,
                                    }}
                                >
                                    {group.title}
                                </Typography>
                                {visibleRows.map((r) => (
                                    <ParamRow
                                        key={r.suffix}
                                        mavId={mavId}
                                        name={`${prefix}${r.suffix}`}
                                        desc={r.desc}
                                        disabled={disabled}
                                        onCommit={onCommit}
                                        paramListTimestamp={paramListTimestamp}
                                        gridTemplateColumns="minmax(120px, auto) minmax(0, 1fr)"
                                        hideDescription
                                    />
                                ))}
                            </Box>
                        );
                    })}
                </Box>
            </Box>
        </Box>
    );
};

const Metric = ({ label, value, unit, icon }) => {
    const hasValue = value != null && value !== '\u2014';
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
            <Typography variant="caption" sx={{ color: 'text.disabled', display: 'flex', alignItems: 'center', gap: 0.25 }}>
                {icon}{label}
            </Typography>
            <Typography
                variant="body2"
                sx={{
                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
                    fontVariantNumeric: 'tabular-nums',
                }}
            >
                {value}
                {unit && hasValue && <Typography component="span" variant="caption" sx={{ color: 'text.secondary', ml: 0.25 }}>{unit}</Typography>}
            </Typography>
        </Box>
    );
};

const Battery = ({ selectedMavId, mavTimestamp, paramListTimestamp }) => {
    const theme = useTheme();
    const [statuses, setStatuses] = useState({}); // { id: BATTERY_STATUS_msg }
    const [editEnabled, setEditEnabled] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);

    // Subscribe directly to mavlink events so we don't miss instances when
    // multiple BATTERY_STATUS messages (id=0, id=1, ...) arrive between
    // React renders (mavTimestamp ticks can collapse and overwrite
    // vehicle.BATTERY_STATUS before our effect sees each one).
    useEffect(() => {
        setStatuses({});
        if (!selectedMavId) return undefined;
        const handler = () => {
            const vehicle = window.mav && window.mav[selectedMavId];
            if (!vehicle) return;
            const msg = vehicle.BATTERY_STATUS;
            if (!msg || msg.id === undefined) return;
            setStatuses((prev) => {
                if (prev[msg.id] === msg) return prev;
                return { ...prev, [msg.id]: msg };
            });
        };
        // Capture whatever was already cached on mount.
        handler();
        window.addEventListener('mav', handler);
        return () => window.removeEventListener('mav', handler);
    }, [selectedMavId]);
    // Keep mavTimestamp referenced so the live-telemetry grid re-renders on every tick.
    // eslint-disable-next-line no-unused-expressions
    mavTimestamp;

    const handleCommit = useCallback((name, value, type) => {
        if (!selectedMavId) return;
        window.api.setParams(selectedMavId, {
            [name]: { value, type },
        });
    }, [selectedMavId]);

    const instances = useMemo(() => {
        const arr = [];
        for (let i = 0; i < NUM_INSTANCES; i++) {
            const prefix = prefixFor(i);
            const monitorEntry = getParamEntry(selectedMavId, `${prefix}MONITOR`);
            const enabled = monitorEntry && Number(monitorEntry.value) !== 0;
            const status = statuses[i] || null;
            arr.push({ idx: i, prefix, enabled, status });
        }
        return arr;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedMavId, paramListTimestamp, statuses]);

    const visible = instances.filter((b) => b.idx < 2 || b.enabled || b.status);
    const liveCount = instances.filter((b) => !!b.status).length;
    const enabledCount = instances.filter((b) => b.enabled).length;

    return (
        <Box
            sx={{
                height: '100%',
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                p: '2px',
                boxSizing: 'border-box',
            }}
        >
            <Box
                sx={{
                    flex: 1,
                    minHeight: 0,
                    p: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    overflow: 'hidden',
                }}
            >
                <Stack direction="row" alignItems="center" spacing={1}>
                    <BatteryIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                    <Typography variant="subtitle2">Battery</Typography>
                    <Chip
                        size="small"
                        label={`${liveCount} live / ${enabledCount} configured`}
                        color={liveCount > 0 ? 'success' : (enabledCount > 0 ? 'primary' : 'default')}
                        sx={{ height: 20, fontSize: 11 }}
                    />
                    <Box sx={{ flex: 1 }} />
                    <FormControlLabel
                        control={
                            <Checkbox
                                size="small"
                                checked={editEnabled}
                                onChange={(e) => {
                                    if (e.target.checked) setConfirmOpen(true);
                                    else setEditEnabled(false);
                                }}
                            />
                        }
                        label={<Typography variant="caption">Enable edit</Typography>}
                        sx={{ mr: 0 }}
                    />
                </Stack>

                <Box
                    sx={{
                        flex: 1,
                        minHeight: 0,
                        overflow: 'auto',
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 1,
                        bgcolor: 'background.default',
                        p: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1.5,
                    }}
                >
                    {!selectedMavId && (
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            No vehicle selected.
                        </Typography>
                    )}
                    {selectedMavId && visible.length === 0 && (
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            No batteries configured or detected. Set BATT_MONITOR to enable a battery monitor.
                        </Typography>
                    )}
                    {selectedMavId && visible.map((b) => (
                        <BatteryCard
                            key={b.idx}
                            idx={b.idx}
                            status={b.status}
                            prefix={b.prefix}
                            mavId={selectedMavId}
                            disabled={!editEnabled}
                            onCommit={handleCommit}
                            theme={theme}
                            paramListTimestamp={paramListTimestamp}
                            forceShow={b.idx < 2}
                        />
                    ))}
                </Box>
            </Box>

            <ConfirmationDialog
                open={confirmOpen}
                onClose={() => setConfirmOpen(false)}
                onConfirm={() => { setEditEnabled(true); setConfirmOpen(false); }}
                title="Enable battery parameter editing?"
                description="Modifying battery monitor configuration, failsafe thresholds or current/voltage calibration while in flight can disable safety triggers or report wrong values. Only proceed if the vehicle is safely on the ground."
                confirmColor="warning"
            />
        </Box>
    );
};

export default Battery;
